import type { AuthProvider } from 'react-admin';

import { readProjectSettings } from './adminSettings';

export type AdminSession = {
    email: string;
    accessToken: string;
    adminToken: string;
    refreshToken?: string;
    profile?: Record<string, unknown>;
    loggedInAt: string;
};

const AUTH_STORAGE_KEY = 'pteultra-admin:auth';

type AuthEnvelope = {
    success?: boolean;
    data?: unknown;
    errorMessage?: string;
    message?: string;
};

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, '');
}

function hasVersionedApiPath(value: string): boolean {
    return /\/api\/v[^/]+$/i.test(trimTrailingSlash(value));
}

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const baseUrl = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
                'https://test-api.pteultra.com/api/v1',
        ),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');

    return { baseUrl, apiVersion };
}

function getApiRoot(): string {
    const { baseUrl, apiVersion } = getAdminApiConfig();
    return hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
}

function getAuthErrorMessage(payload: AuthEnvelope | null, fallback: string): string {
    const candidate = payload?.errorMessage ?? payload?.message;
    return typeof candidate === 'string' && candidate.trim() ? candidate : fallback;
}

function getObjectCandidate(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function getStringCandidate(source: Record<string, unknown> | null, keys: string[]): string {
    if (!source) return '';

    for (const key of keys) {
        const value = source[key];
        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
    }

    return '';
}

function extractAuthData(payload: unknown) {
    const data = getObjectCandidate(payload);
    const accessToken = getStringCandidate(data, [
        'accessToken',
        'token',
        'jwtToken',
        'bearerToken',
        'idToken',
    ]);
    const refreshToken = getStringCandidate(data, ['refreshToken']);
    const email = getStringCandidate(data, [
        'email',
        'emailOrMobile',
    ]);

    return {
        accessToken,
        refreshToken,
        email,
        profile: data ?? undefined,
    };
}

async function requestAuth<T>(path: string, init: RequestInit): Promise<T> {
    const url = new URL(`${getApiRoot()}/${path}`);
    const response = await fetch(url.toString(), {
        ...init,
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...(init.headers ?? {}),
        },
    });

    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();

    if (!response.ok) {
        if (payload && typeof payload === 'object') {
            const problem = payload as Record<string, unknown>;
            const title = typeof problem.title === 'string' ? problem.title : '';
            throw new Error(title || `${response.status} ${response.statusText}`);
        }
        throw new Error(typeof payload === 'string' && payload.trim() ? payload : `${response.status} ${response.statusText}`);
    }

    return payload as T;
}

async function authenticatePassword(email: string, password: string) {
    const payload = await requestAuth<AuthEnvelope>('User/authenticate-password', {
        method: 'POST',
        body: JSON.stringify({
            emailOrMobile: email,
            Password: password,
        }),
    });

    if (payload.success === false) {
        throw new Error(getAuthErrorMessage(payload, 'Login failed'));
    }

    const authData = extractAuthData(payload.data);
    if (!authData.accessToken) {
        throw new Error('Login succeeded but no access token was returned');
    }

    return authData;
}

async function validateSession(accessToken: string): Promise<void> {
    const url = new URL(`${getApiRoot()}/User/is-authenticated`);
    const response = await fetch(url.toString(), {
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
    });

    if (!response.ok) {
        throw new Error('Authentication required');
    }
}

export function readAdminSession(): AdminSession | null {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;

    try {
        return JSON.parse(raw) as AdminSession;
    } catch {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        return null;
    }
}

function writeAdminSession(session: AdminSession): void {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
}

function clearAdminSession(): void {
    localStorage.removeItem(AUTH_STORAGE_KEY);
}

const ADMIN_TAG_KEYS = new Set(['adminTags', 'admin_tags', 'tags']);

function decodeTokenPayload(token: string): Record<string, unknown> | null {
    const payload = token.split('.')[1];
    if (!payload) return null;

    try {
        const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
        return getObjectCandidate(JSON.parse(atob(padded)));
    } catch {
        return null;
    }
}

function normalizeTags(value: unknown): string[] {
    if (Array.isArray(value)) {
        return value
            .map((item) => String(item).trim().toLowerCase())
            .filter(Boolean);
    }
    if (typeof value === 'string') {
        return value
            .split(/[,\s]+/)
            .map((item) => item.trim().toLowerCase())
            .filter(Boolean);
    }
    return [];
}

function collectAdminTags(source: Record<string, unknown> | null, depth = 0): string[] {
    if (!source || depth > 2) return [];
    const tags: string[] = [];

    Object.entries(source).forEach(([key, value]) => {
        if (ADMIN_TAG_KEYS.has(key)) {
            tags.push(...normalizeTags(value));
        } else if (['data', 'profile', 'user', 'claims'].includes(key)) {
            tags.push(...collectAdminTags(getObjectCandidate(value), depth + 1));
        }
    });

    return tags;
}

export function readAdminTags(session = readAdminSession()): string[] {
    if (!session) return [];
    return [...new Set([
        ...collectAdminTags(session.profile ?? null),
        ...collectAdminTags(decodeTokenPayload(session.accessToken)),
    ])];
}

export function hasAnyAdminTag(requiredTags: string[], session = readAdminSession()) {
    const available = new Set(readAdminTags(session));
    return requiredTags.some((tag) => available.has(tag.toLowerCase()));
}

export const authProvider: AuthProvider = {
    async login(params) {
        const email = String(params?.email ?? '').trim();
        const password = String(params?.password ?? '').trim();

        if (!email || !password) {
            throw new Error('Email and password are required');
        }

        const authData = await authenticatePassword(email, password);

        writeAdminSession({
            email: authData.email || email,
            accessToken: authData.accessToken,
            adminToken: authData.accessToken,
            refreshToken: authData.refreshToken,
            profile: authData.profile,
            loggedInAt: new Date().toISOString(),
        });
    },

    async logout() {
        clearAdminSession();
    },

    async checkAuth() {
        const session = readAdminSession();
        if (!session?.accessToken) {
            throw new Error('Authentication required');
        }

        await validateSession(session.accessToken);
    },

    async checkError(error) {
        const status = error?.status;
        if (status === 401 || status === 403) {
            clearAdminSession();
            throw new Error('Your session has expired');
        }
    },

    async getIdentity() {
        const session = readAdminSession();
        if (!session) {
            throw new Error('Authentication required');
        }

        return {
            id: session.email,
            fullName:
                (typeof session.profile?.fullName === 'string' && session.profile.fullName) || session.email,
        };
    },

    async getPermissions() {
        return readAdminTags();
    },
};
